/**
 * How a pull request session's runs read: each run's status from its turn and its FixSession
 * record, the session's status in the sidebar, and which run is current. Pure and client-safe:
 * the read model computes these, and tests pin them without a database.
 */
import type { FixRunStatus, FixSessionStatus } from './types';

import { isWorkingTurnStatus } from '@/lib/code-sessions/turn-utils';

/** A CodeSessionTurn's status (`CodeTurnStatus`), restated so this module stays client-safe. */
export type CodeSessionRunTurnStatus =
  | 'queued'
  | 'admitted'
  | 'running'
  | 'done'
  | 'failed'
  | 'interrupted';

const RUN_TURN_STATUSES: ReadonlySet<string> = new Set<CodeSessionRunTurnStatus>([
  'queued',
  'admitted',
  'running',
  'done',
  'failed',
  'interrupted',
]);

/** Narrows a turn status read as a plain string, as the client's session rows carry it. */
export function isCodeSessionRunTurnStatus(status: string): status is CodeSessionRunTurnStatus {
  return RUN_TURN_STATUSES.has(status);
}

/** A turn that ended before its run had a record: what the run's record would have said. */
const ENDED_TURN_RUN_STATUS = {
  done: 'completed',
  failed: 'failed',
  interrupted: 'cancelled',
} as const satisfies Partial<Record<CodeSessionRunTurnStatus, FixSessionStatus>>;

/**
 * A run on code sessions: its record's outcome once the run finished, since finalization writes
 * it last. Until then its turn tells: waiting its turn is `queued`, admitted or running is
 * `running`, and a turn that ended stays `running` while finalization has yet to record the run.
 */
export function codeSessionRunStatus(
  turnStatus: CodeSessionRunTurnStatus,
  recordStatus: FixSessionStatus | null,
): FixRunStatus {
  if (recordStatus !== null && recordStatus !== 'running') {
    return recordStatus;
  }
  switch (turnStatus) {
    case 'queued':
      return 'queued';
    case 'admitted':
    case 'running':
      return 'running';
    case 'done':
    case 'failed':
    case 'interrupted':
      return recordStatus === 'running' ? 'running' : ENDED_TURN_RUN_STATUS[turnStatus];
    default: {
      const _exhaustive: never = turnStatus;
      return _exhaustive;
    }
  }
}

/** Whether a run is still going or waiting to: what keeps a page or the sidebar refreshing. */
export function isLiveRunStatus(status: FixRunStatus): boolean {
  return status === 'running' || status === 'queued';
}

/** A run as these rules need it: where its turn sits in the session, and how it stands. */
export type SessionRunState = {
  index: number;
  turnStatus: CodeSessionRunTurnStatus;
  status: FixRunStatus;
};

/**
 * One row's status for a pull request session: running while any run is, queued when the only
 * live runs wait their turn, else the latest run's. Null for a session without runs.
 */
export function pullRequestSessionStatus(runs: readonly SessionRunState[]): FixRunStatus | null {
  if (runs.some((run) => run.status === 'running')) {
    return 'running';
  }
  if (runs.some((run) => run.status === 'queued')) {
    return 'queued';
  }
  return latestBy(runs, () => true)?.status ?? null;
}

function latestBy<T extends SessionRunState>(
  runs: readonly T[],
  matches: (run: T) => boolean,
): T | undefined {
  return runs.reduce<T | undefined>(
    (latest, run) => (matches(run) && (!latest || run.index > latest.index) ? run : latest),
    undefined,
  );
}

/**
 * The run a pull request session is on now, whose findings its workspace card lists and whose
 * Stop the header offers: the run the turn engine works on, never a newer one that only waits its
 * turn. With none, the latest run whose turn ended, finished or still being recorded. With nothing
 * run yet, the run that goes next: the earliest queued one. Null for a session without runs.
 */
export function selectCurrentRun<T extends SessionRunState>(runs: readonly T[]): T | null {
  // Turns run one at a time in index order: the lowest working one holds the session.
  const working = runs
    .filter((run) => run.status === 'running' && isWorkingTurnStatus(run.turnStatus))
    .sort((a, b) => a.index - b.index);
  if (working[0]) {
    return working[0];
  }
  // A run is queued until its turn starts, so anything else has ended: finished, or running
  // while finalization records it.
  const ended = latestBy(runs, (run) => run.status !== 'queued');
  if (ended) {
    return ended;
  }
  const queued = runs.filter((run) => run.status === 'queued').sort((a, b) => a.index - b.index);
  return queued[0] ?? null;
}
